import React from 'react';
import { flushSync } from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import FineUploaderS3 from 'fine-uploader-wrappers/s3';
import Cookies from 'js-cookie';
import moment from 'moment';
import ExifReader from 'exifreader';

import {
  APIcreateFile,
  APIcreateFileVersion,
  APIdeleteFile,
} from '../api/Files';

import {
  accountUsageLock,
  getAPIUrl,
  subscriptionExpired,
} from './App';
import { getVisitorId } from './Auth';
import {
  cleanUpExifTags,
  getFileExtension,
  getFileNameFromPath,
  getFolderFromPath,
  isImageFile,
  formatBytes,
  getDefaultMetadataGroupId,
} from './Files';
import { getSetting } from './Settings';
import {
  getAccessTypeFromFiles,
  getCollectionsFromFiles,
  fileObject,
  getStatusFromFiles,
} from './Uploader';

const mime = require('mime');

const withUploader = (WrappedComponent) => {
  class FinalWrappedComponent extends React.Component {
    constructor(props) {
      super(props);

      this.state = {
        submittedFiles: [],
        hasFolder: false,
        uploadCompleted: false,
        fileParams: {},
      };

      this.uploader = null;
      this.initUploader();

      this.resetUploader = this.resetUploader.bind(this);
      this.setFileParams = this.setFileParams.bind(this);
      this.handleRetryFile = this.handleRetryFile.bind(this);
    }

    componentDidMount() {
      this.uploader.on('submitted', (id) => {
        const { submittedFiles } = this.state;
        const { uploadBatchId } = this.props;
        const file = this.uploader.methods.getFile(id);
        const newFile = Object.assign({}, fileObject);
        newFile.id = id;
        newFile.uuid = this.uploader.methods.getUuid(id);
        newFile.original_name = getFileNameFromPath(this.uploader.methods.getName(id));
        newFile.display_name = getFileNameFromPath(this.uploader.methods.getName(id));
        newFile.readable_size = formatBytes(this.uploader.methods.getSize(id));
        newFile.extension = getFileExtension({ path: this.uploader.methods.getName(id) });
        newFile.collections = getCollectionsFromFiles(submittedFiles);
        const fileAccessType = getAccessTypeFromFiles(submittedFiles);
        newFile.public = fileAccessType.public;
        newFile.metadata_group = getDefaultMetadataGroupId();
        newFile.status = getStatusFromFiles(submittedFiles);
        newFile.folder = getFolderFromPath(file.hdcFullPath);
        newFile.batchId = uploadBatchId;
        newFile.errors = {
          taxonomy: [],
        };
        flushSync(() => {
          this.setState(prevState => ({
            submittedFiles: prevState.submittedFiles.concat([newFile]),
            uploadCompleted: false,
          }));
        });

        if (newFile.folder) {
          this.setState({ hasFolder: true });
        }

        const { profile } = this.props;
        this.uploader.methods.setParams({
          client: profile.client.uuid,
          user: profile.uuid,
        }, id);
      });

      this.uploader.on('validate', ({ name }) => {
        if (getSetting('cs1')) {
          return this.removeVoiceTags(name);
        }

        const { profile } = this.props;
        if (accountUsageLock(profile.client) || subscriptionExpired(profile.client)) {
          return false;
        }

        return true;
      });

      this.uploader.on('submit', () => {
        const { profile } = this.props;
        if (accountUsageLock(profile.client) || subscriptionExpired(profile.client)) {
          return false;
        }

        return true;
      });

      this.uploader.on('error', (id) => {
        const { submittedFiles } = this.state;

        const newSubmittedFiles = submittedFiles.map(sf => {
          if (sf.id === id) {
            return {
              ...sf,
              uploadStatus: 'failed',
            };
          }
          return sf;
        });

        this.setState({ submittedFiles: newSubmittedFiles });

        return true;
      });

      this.uploader.on('cancel', (id) => {
        const { uploadPage } = this.props;
        const { submittedFiles } = this.state;

        const file = submittedFiles.find(sf => (sf.id === id));
        if (file && file.uuid && file.uploadStatus === 'completed') {
          APIdeleteFile(file.uuid, { upload_page_uuid: uploadPage?.uuid });
        }
        this.setState({ submittedFiles: submittedFiles.filter(sf => (sf.id !== id)) });

        return true;
      });

      this.uploader.on('complete', (id, name, response) => {
        if (!response.success) {
          return;
        }

        const {
          submittedFiles,
          fileParams,
        } = this.state;
        const file = this.uploader.methods.getFile(id);
        const fileURL = URL.createObjectURL(file);
        const fileAccessType = getAccessTypeFromFiles(submittedFiles);

        const fileMeta = {
          key: this.uploader.methods.getKey(id),
          path: this.uploader.methods.getKey(id),
          uuid: this.uploader.methods.getUuid(id),
          original_name: getFileNameFromPath(this.uploader.methods.getName(id)),
          bucket: this.uploader.methods.getBucket(id),
          type: file.type,
          size: file.size,
          public: fileAccessType.public,
          vid: getVisitorId(),
          lastModifiedDate: moment(file.lastModified).format('YYYY-MM-DD HH:mm:ss'),
          parent_uuid: file.parent_uuid,
          ...fileParams,
        };

        if (!fileMeta.type) {
          fileMeta.type = mime.getType(this.uploader.methods.getName(id));
        }

        if (isImageFile(fileMeta.type) || (!fileMeta.type && fileMeta.size < 100 * 1024 * 1024)) {
          const fileReader = new FileReader();
          fileReader.readAsArrayBuffer(file);
          fileReader.onloadend = (e) => {
            if (e.target.readyState === FileReader.DONE) {
              let exifTags = {};
              if (fileMeta.type !== 'image/gif') {
                try {
                  exifTags = ExifReader.load(e.target.result);
                } catch {
                  exifTags = {};
                }

                if (!fileMeta.type && exifTags.format && exifTags.format.value) {
                  fileMeta.type = exifTags.format.value;
                }

                if (Object.keys(exifTags).length > 0) {
                  exifTags = cleanUpExifTags(exifTags);
                  fileMeta.meta = btoa(unescape(encodeURIComponent(JSON.stringify(exifTags))));
                } else {
                  fileMeta.meta = null;
                }
              }

              if (fileMeta.type === 'image/tiff') {
                if (exifTags.ImageWidth) {
                  fileMeta.width = exifTags.ImageWidth.description;
                }
                if (exifTags.ImageLength) {
                  fileMeta.height = exifTags.ImageLength.description;
                }
                this.createFile(id, fileMeta);
              } else {
                const image = new Image();
                image.src = fileURL;
                image.onload = () => {
                  fileMeta.width = image.width;
                  fileMeta.height = image.height;
                  this.createFile(id, fileMeta);
                };
                image.onerror = () => {
                  fileMeta.type = null;
                  this.createFile(id, fileMeta);
                };
              }
            }
          };
        } else {
          fileMeta.meta = null;
          this.createFile(id, fileMeta);
        }
      });

      this.uploader.on('allComplete', (succeeded, failed) => {
        this.setState({
          uploadCompleted: failed.length === 0,
        });
      });
    }

    handleFileCreated(id, response) {
      const { submittedFiles } = this.state;
      const newSubmittedFiles = submittedFiles.map(file => {
        if (file.id === id) {
          return {
            ...file,
            uuid: response.data.uuid,
            displayName: response.data.display_name,
            path: response.data.path,
            extension: response.data.extension,
            urls: response.data.urls,
            attributes: response.data.attributes,
            uploadStatus: 'completed',
          };
        }
        return file;
      });
      flushSync(() => {
        this.setState({ submittedFiles: newSubmittedFiles });
      });
    }

    handleFileError(id) {
      const { submittedFiles } = this.state;
      const newSubmittedFiles = submittedFiles.map(file => {
        if (file.id === id) {
          return {
            ...file,
            uploadStatus: 'failed',
          };
        }
        return file;
      });
      this.setState({ submittedFiles: newSubmittedFiles });
    }

    createFileNew(id, fileMeta) {
      APIcreateFile(fileMeta).then((response) => {
        if (response.data) {
          this.handleFileCreated(id, response);
        }
      }).catch(() => {
        this.handleFileError(id);
      });
    }

    createFileVersion(id, fileMeta) {
      APIcreateFileVersion(fileMeta.parent_uuid, fileMeta).then((response) => {
        if (response.data) {
          this.handleFileCreated(id, response);
        }
      }).catch(() => {
        this.handleFileError(id);
      });
    }

    createFile(id, fileMeta) {
      if (fileMeta.parent_uuid) {
        this.createFileVersion(id, fileMeta);
      } else {
        this.createFileNew(id, fileMeta);
      }
    }

    setFileParams(fileParams) {
      this.setState({ fileParams });
    }

    removeVoiceTags(name) {
      const { modalMode } = this.props;
      const { submittedFiles } = this.state;
      let result = true;

      if (modalMode === 'edit' || modalMode === 'link') {
        return true;
      }

      let filteredSubmittedFiles = submittedFiles;

      const fileExtension = name.split('.').pop().toUpperCase();
      const fileName = (name.toLowerCase().split('.').slice(0, -1)).join('.');
      if (['WAV', 'MP3'].includes(fileExtension)) {
        const relatedFiles = filteredSubmittedFiles.filter(sf => (
          sf.original_name.toLowerCase().indexOf(fileName) >= 0 &&
          sf.original_name !== name
        ));
        if (relatedFiles.length > 0) {
          result = false;
        }
      } else {
        const audioFiles = filteredSubmittedFiles.filter((sf) => {
          const extension = sf.original_name.split('.').pop().toUpperCase();
          return ['WAV', 'MP3'].includes(extension) && sf.original_name.toLowerCase().indexOf(fileName) >= 0;
        });
        audioFiles.forEach((af) => {
          this.uploader.methods.cancel(af.id);
          this.uploader.methods.setStatus(af.id, this.uploader.qq.status.DELETED);
          if (af.uuid) {
            APIdeleteFile(af.uuid);
          }
          filteredSubmittedFiles = filteredSubmittedFiles.filter(sf => (sf.id !== af.id));
        });
        this.setState({ submittedFiles: filteredSubmittedFiles });
      }

      return result;
    }

    initUploader() {
      this.uploader = new FineUploaderS3({
        options: {
          debug: process.env.MIX_ENV !== 'production',
          validation: {
            stopOnFirstInvalidFile: false,
            allowedExtensions: getSetting('allowed_extensions'),
          },
          request: {
            endpoint: `https://${process.env.MIX_AWS_S3_BUCKET_NAME}.s3.amazonaws.com`,
            accessKey: `${getSetting('upload_key') || process.env.MIX_AWS_ACCESS_KEY_ID}`,
          },
          objectProperties: {
            key: (id) => {
              const { profile } = this.props;
              const ext = this.uploader.qq.getExtension(this.uploader.methods.getName(id));
              const uuid = this.uploader.methods.getUuid(id);
              return `${profile.client.uuid}/${uuid.substring(0, 3)}/${uuid}${ext ? `.${ext}` : ''}`;
            },
          },
          signature: {
            endpoint: getAPIUrl('/upload'),
            version: 4,
            customHeaders: () => ({
              SPAuth: true,
              Authorization: `Bearer ${Cookies.get('sp_access_token')}`,
            }),
          },
          chunking: {
            enabled: true,
            concurrent: {
              enabled: true,
            },
            partSize: (id) => {
              let partSize = 10485760;
              const fileSize = this.uploader.methods.getSize(id);
              if (fileSize > partSize * 9500) {
                partSize = Math.round(fileSize / 9500);
              }
              return partSize;
            },
          },
        },
      });
    }

    handleRetryFile(id) {
      const { submittedFiles } = this.state;

      const file = submittedFiles.find(sf => (sf.id === id));
      file.uploadStatus = 'uploading';
      this.setState({ submittedFiles });
      this.uploader.methods.retry(id);
    }

    resetUploader() {
      this.setState({
        submittedFiles: [],
        hasFolder: false,
        uploadCompleted: false,
      });
      this.uploader.methods.clearStoredFiles();
      this.uploader.methods.reset();
    }

    render() {
      const {
        submittedFiles,
        hasFolder,
        uploadCompleted,
      } = this.state;

      return (
        <WrappedComponent
          {...this.props}
          uploader={this.uploader}
          submittedFiles={submittedFiles}
          hasFolder={hasFolder}
          uploadCompleted={uploadCompleted}
          setSubmittedFiles={(sf) => this.setState({ submittedFiles: sf })}
          handleRetryFile={this.handleRetryFile}
          resetUploader={this.resetUploader}
          setFileParams={this.setFileParams}
        />
      );
    }
  }

  const mapStateToProps = state => ({
    metadataGroups: state.Metadata.metadataGroups,
    uploadPage: state.UploadPages.uploadPage,
    uploadBatchId: state.UploadPages.uploadBatchId,
  });

  FinalWrappedComponent.propTypes = {
    metadataGroups: PropTypes.array.isRequired,
    uploadPage: PropTypes.object,
    uploadBatchId: PropTypes.number.isRequired,
    profile: PropTypes.object,
    modalMode: PropTypes.string,
  };

  FinalWrappedComponent.defaultProps = {
    uploadPage: null,
    profile: null,
    modalMode: 'upload',
  };

  return connect(mapStateToProps)(FinalWrappedComponent);
};

export default withUploader;
